import {
  BodyText,
  Callout,
  HeaderText,
  Separator,
  Skeleton,
  toast,
} from "@/components/ui";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components/EditableRecordButton";
import { formatPhoneNumber } from "@/lib/formatters";
import { Patient } from "@curai/js-fhir-tools";
import { useState } from "react";
import type { z } from "zod";
import {
  type EmergencyContact,
  useGetEmergencyContact,
  useUpdateEmergencyContact,
} from "../network/useEmergencyContact";
import type { emergencyContactFormSchema } from "../schemas";
import { UpdateEmergencyContactForm } from "./UpdateEmergencyContactForm";

export const EmergencyContactEditor = ({
  patientId,
}: {
  patientId: string;
}) => {
  const [editing, setEditing] = useState(false);
  const {
    data: emergencyContact,
    isLoading,
    isError,
    refetch,
  } = useGetEmergencyContact(patientId);

  const { mutate: updateEmergencyContact, isPending: isUpdating } =
    useUpdateEmergencyContact(patientId, () => {
      setEditing(false);
      toast.success("Emergency contact updated");
    });

  const handleSubmit = (values: z.infer<typeof emergencyContactFormSchema>) => {
    updateEmergencyContact(values as EmergencyContact);
  };

  if (isLoading)
    return (
      <div className="py-2 px-3 w-full">
        <Skeleton className="h-24" />
      </div>
    );

  if (isError || !emergencyContact) {
    return (
      <div className="flex flex-col gap-2 py-2 w-full">
        <Callout
          className="w-full cursor-pointer"
          variant="danger"
          title="Unable to load emergency contact!"
          description="Please click here to try again."
          onClick={() => refetch()}
        />
      </div>
    );
  }

  return editing ? (
    <UpdateEmergencyContactForm
      isUpdating={isUpdating}
      onCancel={() => setEditing(false)}
      handleSubmit={handleSubmit}
      initialValues={{
        contactName: emergencyContact.contactName ?? "",
        contactPhone: emergencyContact.contactPhone ?? "",
        contactRelationshipRoleType:
          emergencyContact.contactRelationshipRoleType ?? "",
      }}
    />
  ) : (
    <EditableRecordButton
      onClick={() => {
        setEditing(true);
      }}
    >
      <div className="flex w-full">
        <div className="w-full">
          <HeaderText size="sm">Name</HeaderText>
          <BodyText size="sm">
            {emergencyContact.contactName || "Not Set"}
          </BodyText>
        </div>
        <div className="w-full ml-6">
          <HeaderText size="sm">Relationship</HeaderText>
          <BodyText size="sm">
            {emergencyContact.contactRelationshipRoleType
              ? Patient.RelationshipRoleTypeLabels[
                  emergencyContact.contactRelationshipRoleType
                ]
              : "Not Set"}
          </BodyText>
        </div>
      </div>
      <Separator />
      <div className="flex w-full">
        <div className="w-full sm:w-1/2">
          <HeaderText size="sm">Phone Number</HeaderText>
          <BodyText size="sm">
            {emergencyContact.contactPhone
              ? formatPhoneNumber(emergencyContact.contactPhone)
              : "Not Set"}
          </BodyText>
        </div>
      </div>
    </EditableRecordButton>
  );
};
